import { useCallback, useMemo } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardCheck,
  Clock,
  Lightbulb,
  LineChart,
  Loader2,
  User,
  Users,
} from "lucide-react";
import { SchoolLink } from "@/components/DrilldownLink";
import { splitIntoInsightBuckets } from "@/components/AiInsightsCard";
import InterventionActionPlan from "@/components/InterventionActionPlan";
import HeroStatusChip from "@/components/HeroStatusChip";
import { cardsToInsightLines, registerToInsightLines } from "@/utils/insightActionPlanDefaults";
import { useMistralFirstInsights } from "@/hooks/useMistralFirstInsights";
import { parseInsightBullets } from "@/utils/parseInsightBullets";
import {
  bulletsToActionQueue,
  bulletsToApaarAiFindings,
  bulletsToApaarRootCauseSignals,
  bulletsToApaarSummaryCards,
  bulletsToApaarTrackerRows,
} from "@/utils/mistralInsightMappers";
import {
  buildApaarAiFindings,
  buildApaarInsightTrackerRows,
  buildApaarInsightsActionQueue,
  buildApaarInsightsHeroStats,
  buildApaarInsightsMarkdown,
  buildApaarInsightsSummaryCards,
  buildApaarPriorityWatchlist,
  buildApaarRootCauseSignals,
} from "@/utils/apaarDerived";
import { formatDataFreshnessLabel } from "@/utils/dataFreshness";
import "./ApaarInsightsTab.css";
import { toTitleCase } from "../utils/toTitleCase";

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const SUMMARY_ICONS = {
  green: LineChart,
  orange: Clock,
  purple: Users,
  red: AlertTriangle,
};

const KPI_ICONS = {
  green: CheckCircle2,
  orange: Clock,
  red: AlertTriangle,
};

const FINDING_ICONS = {
  orange: Clock,
  purple: User,
  red: AlertTriangle,
};

const ACTION_ICONS = {
  purple: Users,
  orange: ClipboardCheck,
  red: AlertTriangle,
};

const ApaarInsightsTab = ({ overview = {}, riskData = {}, lowPerforming = [] }) => {
  const hasData = Boolean(overview?.total_students);

  const buildLocalMarkdown = useCallback(
    () => buildApaarInsightsMarkdown(overview, riskData, lowPerforming),
    [overview, riskData, lowPerforming]
  );

  const scopeKey = useMemo(
    () =>
      JSON.stringify({
        g: overview?.generation_rate,
        p: overview?.total_pending,
        s: overview?.total_students,
      }),
    [overview?.generation_rate, overview?.total_pending, overview?.total_students]
  );

  const {
    markdown: aiMarkdown,
    loading: insightsLoading,
    generatedAt,
    isMistral,
  } = useMistralFirstInsights({
    kind: "apaar",
    title: "APAAR Analytics Insights",
    buildLocalMarkdown,
    context: { overview, riskData, lowPerforming },
    enabled: hasData,
    scopeKey,
  });

  const buckets = useMemo(() => splitIntoInsightBuckets(aiMarkdown), [aiMarkdown]);

  const localHeroStats = useMemo(
    () => buildApaarInsightsHeroStats(overview, riskData, lowPerforming),
    [overview, riskData, lowPerforming]
  );

  const heroStats = useMemo(() => {
    if (insightsLoading) return localHeroStats;
    const insightBullets = parseInsightBullets(buckets.insights);
    const primaryInsight = insightBullets[0] || localHeroStats.primaryInsight;
    const statusChips = [...localHeroStats.statusChips];
    if (isMistral) {
      statusChips.unshift({ label: "Mistral AI Assisted", className: "purple" });
    }
    return { ...localHeroStats, primaryInsight, statusChips };
  }, [insightsLoading, buckets.insights, localHeroStats, isMistral]);

  const localSummaryCards = useMemo(() => buildApaarInsightsSummaryCards(overview), [overview]);
  const summaryCards = useMemo(() => {
    if (insightsLoading) return [];
    const aiCards = bulletsToApaarSummaryCards(
      parseInsightBullets(buckets.insights),
      localSummaryCards
    );
    return aiCards?.length ? aiCards : localSummaryCards;
  }, [insightsLoading, buckets.insights, localSummaryCards]);

  const localRootCauseSignals = useMemo(() => buildApaarRootCauseSignals(overview), [overview]);
  const rootCauseSignals = useMemo(() => {
    if (insightsLoading) return [];
    const aiRows = bulletsToApaarRootCauseSignals(
      parseInsightBullets(buckets.root),
      localRootCauseSignals
    );
    return aiRows?.length ? aiRows : localRootCauseSignals;
  }, [insightsLoading, buckets.root, localRootCauseSignals]);

  const localAiFindings = useMemo(() => buildApaarAiFindings(overview), [overview]);
  const aiFindings = useMemo(() => {
    if (insightsLoading) return [];
    const fromRoot = bulletsToApaarAiFindings(
      parseInsightBullets(buckets.root),
      localAiFindings
    );
    const fromInsights = bulletsToApaarAiFindings(
      parseInsightBullets(buckets.insights),
      localAiFindings
    );
    return fromRoot?.length ? fromRoot : fromInsights?.length ? fromInsights : localAiFindings;
  }, [insightsLoading, buckets.root, buckets.insights, localAiFindings]);

  const watchlist = useMemo(
    () => buildApaarPriorityWatchlist(lowPerforming, riskData),
    [lowPerforming, riskData]
  );

  const localActionQueue = useMemo(
    () => buildApaarInsightsActionQueue(overview, riskData, lowPerforming),
    [overview, riskData, lowPerforming]
  );
  const actionQueue = useMemo(() => {
    if (insightsLoading) return [];
    const aiActions = bulletsToActionQueue(
      parseInsightBullets(buckets.recs).length
        ? parseInsightBullets(buckets.recs)
        : parseInsightBullets(buckets.actions),
      localActionQueue
    );
    return aiActions?.length ? aiActions : localActionQueue;
  }, [insightsLoading, buckets.recs, buckets.actions, localActionQueue]);

  const localTrackerRows = useMemo(
    () => buildApaarInsightTrackerRows(overview, riskData, lowPerforming),
    [overview, riskData, lowPerforming]
  );
  const trackerRows = useMemo(() => {
    if (insightsLoading) return [];
    const aiRows = bulletsToApaarTrackerRows(
      parseInsightBullets(buckets.actions),
      localTrackerRows
    );
    return aiRows?.length ? aiRows : localTrackerRows;
  }, [insightsLoading, buckets.actions, localTrackerRows]);

  const actionFooter = useMemo(() => {
    if (insightsLoading) return "Generating AI recommendations…";
    const bullets = parseInsightBullets(buckets.actions);
    return (
      bullets[bullets.length - 1] ||
      "Conduct a daily 15-minute APAAR war-room review until pending backlog reduces by at least 25%."
    );
  }, [insightsLoading, buckets.actions]);

  const defaultRoot = useMemo(
    () => cardsToInsightLines(localRootCauseSignals),
    [localRootCauseSignals]
  );
  const defaultRecs = useMemo(() => cardsToInsightLines(localAiFindings), [localAiFindings]);
  const defaultActions = useMemo(
    () => cardsToInsightLines(localActionQueue),
    [localActionQueue]
  );

  const freshnessLabel = useMemo(
    () =>
      insightsLoading
        ? "Generating insights…"
        : generatedAt
          ? formatDataFreshnessLabel(generatedAt)
          : "Ready",
    [insightsLoading, generatedAt]
  );

  if (!hasData) {
    return (
      <div className="apin-insights-tab" data-testid="apaar-insights">
        <p style={{ textAlign: "center", padding: "48px 24px", color: "#64748b" }}>
          No APAAR insights data available for the selected scope.
        </p>
      </div>
    );
  }

  return (
    <div className="apin-insights-tab" data-testid="apaar-insights">
      <section className="apin-hero-card" id="apin-insights-hero">
        <div className="apin-hero-layout">
          <div>
            <div className="apin-insight-main">
              <div
                className="apin-gauge"
                style={heroStats.gaugeStyle}
                aria-label={`APAAR Generation Rate ${heroStats.districtRate} percent`}
              >
                <div>
                  <strong>{heroStats.districtRate}%</strong>
                  <span>Gen Rate</span>
                </div>
              </div>
              <div className="apin-insight-copy">
                <h2 className="dashboard-kpi-hero-title">APAAR Insight Control Center</h2>
                <p>
                  Mistral AI enriches rule-based APAAR metrics into an executive triage view: what
                  changed, where risk is concentrated, and which officer action should happen first.
                </p>
                <div className="apin-status-row">
                  {heroStats.statusChips.map((chip) => {
                    const actionable = /red|orange|amber/i.test(chip.className ?? "");
                    return (
                      <HeroStatusChip
                        key={chip.label}
                        chipClass="apin-status-chip"
                        variant={chip.className || undefined}
                        onClick={actionable ? () => scrollToId("apin-actions") : undefined}
                        ariaLabel={actionable ? `${chip.label} — view APAAR actions` : undefined}
                      >
                        {chip.label}
                      </HeroStatusChip>
                    );
                  })}
                  {insightsLoading ? (
                    <HeroStatusChip chipClass="apin-status-chip" showChevron={false}>
                      <Loader2 size={12} className="animate-spin" style={{ display: "inline" }} />{" "}
                      {freshnessLabel}
                    </HeroStatusChip>
                  ) : (
                    <HeroStatusChip chipClass="apin-status-chip" variant="blue" showChevron={false}>
                      {freshnessLabel}
                    </HeroStatusChip>
                  )}
                </div>
              </div>
            </div>
            <div className="apin-summary-note">
              <div className="apin-icon-box purple">
                <Lightbulb size={22} />
              </div>
              <div>
                <b>Primary insight</b>
                <span>{heroStats.primaryInsight}</span>
              </div>
            </div>
          </div>

          <div className="apin-metric-grid" aria-label="Insight KPIs">
            {heroStats.kpis.map((card) => {
              const Icon = KPI_ICONS[card.iconClass] || CheckCircle2;
              return (
                <article key={card.label} className={`apin-metric-card ${card.cardClass}`.trim()}>
                  <div className="apin-metric-top">
                    <div>
                      <small>{card.label}</small>
                      <strong>{card.value}</strong>
                    </div>
                    <span className={`apin-mini-icon ${card.iconClass}`.trim()}>
                      <Icon size={22} />
                    </span>
                  </div>
                  <span>{card.sub}</span>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <div className="apin-section-title" id="apin-insight-summary">
        <div>
          <h2>AI Insight Summary</h2>
          <p>
            Insights are generated via Mistral AI first, with rule-based fallback when the API is
            unavailable. Metrics and bar widths stay derived from live data.
          </p>
        </div>
        <button
          type="button"
          className="apin-section-action"
          onClick={() => scrollToId("apin-actions")}
        >
          View action queue →
        </button>
      </div>

      <section className="apin-insight-grid">
        {insightsLoading ? (
          <div className="apin-insight-card" style={{ gridColumn: "1 / -1", textAlign: "center" }}>
            <Loader2 size={28} className="animate-spin" style={{ margin: "24px auto" }} />
            <p>Generating Mistral insights…</p>
          </div>
        ) : (
          summaryCards.map((card) => {
            const Icon = SUMMARY_ICONS[card.iconClass] || LineChart;
            return (
              <article key={card.id} className="apin-insight-card">
                <div className="apin-insight-head">
                  <div className={`apin-icon-box ${card.iconClass}`.trim()}>
                    <Icon size={22} />
                  </div>
                  <span className={`apin-severity ${card.severityClass}`.trim()}>{card.severity}</span>
                </div>
                <h3>{toTitleCase(card.title)}</h3>
                <p>{card.body}</p>
                <div className="apin-progress-line">
                  <i style={{ width: `${card.progress}%`, background: card.progressColor }} />
                </div>
                <div className="apin-card-meta">
                  <span>{card.metaLabel}</span>
                  <span>{card.metaValue}</span>
                </div>
              </article>
            );
          })
        )}
      </section>

      <div className="apin-section-title" id="apin-diagnostics">
        <div>
          <h2>Root-Cause Diagnostics</h2>
          <p>
            Signal strength bars use live metrics; narrative labels prefer Mistral with rule-based
            fallback.
          </p>
        </div>
        <button
          type="button"
          className="apin-section-action"
          onClick={() => scrollToId("apin-tracker-table")}
        >
          Go to owner table →
        </button>
      </div>

      <section className="apin-analytics-grid">
        <article className="apin-panel">
          <div className="apin-panel-head">
            <div>
              <h3>Root Cause Signal Strength</h3>
              <p>Prioritized signal view for district and block review meetings.</p>
            </div>
            <span className="apin-pill">AI Signals</span>
          </div>
          <div className="apin-bar-list" aria-label="Root cause signal bars">
            {(insightsLoading ? [] : rootCauseSignals).map((signal) => (
              <div key={signal.label} className="apin-hbar-row">
                <div className="apin-hbar-label">{signal.label}</div>
                <div className="apin-hbar-track">
                  <div
                    className={`apin-hbar ${signal.barClass}`.trim()}
                    style={{ width: `${signal.widthPct}%` }}
                  />
                </div>
                <div className="apin-hbar-value">{signal.value}</div>
              </div>
            ))}
          </div>
        </article>

        <article className="apin-panel">
          <div className="apin-panel-head">
            <div>
              <h3>AI Findings</h3>
              <p>Key messages rewritten as operational findings.</p>
            </div>
            <span className="apin-pill">{isMistral ? "Mistral AI" : "Rule-based"}</span>
          </div>
          <div className="apin-ai-finding-list">
            {(insightsLoading ? [] : aiFindings).map((finding) => {
              const Icon = FINDING_ICONS[finding.iconClass] || Clock;
              return (
                <div key={finding.title} className="apin-ai-finding">
                  <div className={`apin-icon-box ${finding.iconClass}`.trim()}>
                    <Icon size={22} />
                  </div>
                  <div>
                    <h4>{finding.title}</h4>
                    <p>{finding.body}</p>
                  </div>
                  <span className="apin-owner-tag">{finding.owner}</span>
                </div>
              );
            })}
          </div>
        </article>
      </section>

      <section className="apin-action-plan">
        <InterventionActionPlan
          aiContent={aiMarkdown}
          defaultRoot={defaultRoot}
          defaultRecs={defaultRecs}
          defaultActions={defaultActions}
        />
      </section>

      <section className="apin-two-column" id="apin-actions">
        <article className="apin-panel">
          <div className="apin-panel-head">
            <div>
              <h3>Priority School Watchlist</h3>
              <p>Schools surfaced from live low-performing and risk data.</p>
            </div>
            <span className="apin-pill">Immediate</span>
          </div>
          <div className="apin-watchlist">
            {watchlist.map((school) => (
              <div key={school.udise_code || school.school_name} className="apin-school-card">
                <strong>
                  {school.udise_code ? (
                    <SchoolLink udiseCode={school.udise_code}>{school.school_name}</SchoolLink>
                  ) : (
                    school.school_name
                  )}
                </strong>
                <span>
                  {school.block_name} · {school.note}
                </span>
                <b>{school.generation_rate}</b>
              </div>
            ))}
          </div>
        </article>

        <article className="apin-panel">
          <div className="apin-panel-head">
            <div>
              <h3>48-Hour Recommended Actions</h3>
              <p>Clear sequence for district-level review and closure.</p>
            </div>
            <span className="apin-pill">Priority</span>
          </div>
          <div className="apin-action-card">
            {(insightsLoading ? [] : actionQueue).map((action) => {
              const Icon = ACTION_ICONS[action.iconClass] || ClipboardCheck;
              return (
                <div key={action.title} className="apin-action-item">
                  <div className={`apin-icon-box ${action.iconClass}`.trim()}>
                    <Icon size={22} />
                  </div>
                  <div>
                    <h4>{action.title}</h4>
                    <p>{action.body}</p>
                  </div>
                </div>
              );
            })}
            <div className="apin-action-footer">
              <b>Next review recommendation</b>
              <span>{actionFooter}</span>
            </div>
          </div>
        </article>
      </section>

      <section className="apin-panel apin-table-card" id="apin-tracker-table">
        <div className="apin-table-head">
          <div>
            <h3>Insight Owner and Action Tracker</h3>
            <p>
              Actionable version of Root Cause Signals, Recommendations, and Priority Action Items.
            </p>
          </div>
          <span className="apin-pill">Execution View</span>
        </div>
        <table aria-label="Insight action tracker">
          <thead>
            <tr>
              <th>Priority</th>
              <th>Insight</th>
              <th>Evidence</th>
              <th>Primary Owner</th>
              <th>Recommended Action</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {(insightsLoading ? [] : trackerRows).map((row) => (
              <tr key={row.priority}>
                <td className="apin-score">{row.priority}</td>
                <td>{row.insight}</td>
                <td>{row.evidence}</td>
                <td>{row.owner}</td>
                <td>{row.action}</td>
                <td>
                  <span className={`apin-tag ${row.tagClass}`.trim()}>{row.status}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
};

export default ApaarInsightsTab;
